/** Angular Imports */
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    catchError,
    combineLatest,
    debounceTime,
    delay,
    distinctUntilChanged,
    map,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs';

/** Shared Utils */
import { BaseQueryParams } from '@shared';
import { QueryFilter } from '@shared/interfaces/filter-operator.interface';
import { buildQueryParams, fetchAllBatch, formatDateDisplay, Logger } from '@shared/utils';
import { HostsService } from '../hosts/hosts.service';
import { SharedService } from '../shared/shared.service';

/** Local Imports */
import { CreateChecklistDto, CreateEntityChecklistDto } from '@core/dto';
import { PaginatedResponse } from '@core/services/interfaces/pagination.interface';
import { LICENSE_SEARCH_FIELDS } from '@features/licenses/constants/licenses-table.constants';
import { UILicense } from '@features/licenses/interfaces/ui-license-table.interface';
import { ApiHostLocation } from '../hosts/hosts.interfaces';
import { ApiGetAllResponse, ApiSingleResponse } from '../interfaces';
import {
    ApiChecklists,
    ApiEntityChecklist,
    ApiLicense,
    ApiLicensePlaylistContent,
    ApiLicenseStats,
    CreateLicenseDto,
    DeviceInfo,
    DeviceStatus,
    InternetMetric,
    LicenseQueryParams,
    LicensesCacheType,
    LicenseStats,
    LicenseTimestamp,
    PlayerInfo,
    UpdateLicenseDto,
} from './licenses.interfaces';
/**
 * Service for managing license API operations and device configurations.
 *
 * @description
 * Handles the full lifecycle of licenses, device statuses, and checklists.
 * Implements a sophisticated caching strategy using `LicensesCacheType` and
 * the SharedService's centralized observable cache.
 *
 * @remarks
 * All requests go through the BFF proxy (`/api/...`) which forwards
 * them to the AWS backend. Never hardcode the backend URL directly.
 *
 * @example
 * ```typescript
 * private readonly _licensesService = inject(LicensesService);
 *
 * // Fetch all licenses
 * this._licensesService.getAll().subscribe(licenses => { ... });
 *
 * // Fetch a single license
 * this._licensesService.getById('123').subscribe(license => { ... });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class LicensesService {
    /**
     * Angular HTTP client for API communication.
     * @type {HttpClient}
     */
    private readonly _http = inject(HttpClient);
    /**
     * Shared service for common utilities.
     */
    private readonly _sharedService = inject(SharedService);
    /**
     * Injected hosts service for resolving host-location/timezone lookups.
     */
    private readonly _hostsService = inject(HostsService);

    /**
     * Base URL for the licenses API endpoints.
     * Routed through the BFF proxy in `server.ts`.
     */
    private readonly _baseUrl = '/api/licenses';
    private readonly _baseUrlChecklist = '/api/checklists';
    private readonly _baseUrlEntityChecklist = '/api/entity-checklists';
    private readonly _baseUrlDeviceStatuses = '/api/device-statuses';
    private readonly _baseUrlDeviceInfo = '/api/device-info';
    private readonly _baseUrlTimestamp = '/api/license-timestamps';
    /**
     * In-memory cache for API responses, keyed by endpoint + license ID.
     * Uses shareReplay(1) to replay the last emitted value to new subscribers.
     */
    private readonly _cache = new Map<string, Observable<LicensesCacheType>>();

    /**
     * Retrieves a paginated list of licenses.
     *
     * @param params - Optional query parameters for pagination, sorting, and filtering
     * @returns Observable of paginated license response
     */
    public getAll(params?: LicenseQueryParams): Observable<ApiGetAllResponse<ApiLicense>> {
        const { omitDefaultLimit, ...rest } = params ?? {};
        const mergedParams: LicenseQueryParams = { ...rest };
        if (!omitDefaultLimit && mergedParams.limit === undefined) {
            mergedParams.limit = 15;
        }
        const queryParams = buildQueryParams(mergedParams);
        const cacheKey = `getAll-${JSON.stringify(mergedParams)}`;

        return this._sharedService.getCached(this._cache, cacheKey, () =>
            this._http.get<ApiGetAllResponse<ApiLicense>>(this._baseUrl, { params: queryParams }),
        );
    }

    /**
     * Retrieves aggregated license stats for the dashboard, including player versions.
     *
     * @returns Observable of the license stats payload with player UI and Server versions
     */
    public getStats(): Observable<
        LicenseStats & {
            playerUi: { currentVersion: string; dateCreated: string };
            playerServer: { currentVersion: string; dateCreated: string };
        }
    > {
        return this._sharedService.getCached(this._cache, 'stats', () =>
            this._http
                .get<
                    ApiSingleResponse<
                        LicenseStats & {
                            playerUi: { currentVersion: string; dateCreated: string };
                            playerServer: { currentVersion: string; dateCreated: string };
                        }
                    >
                >(`${this._baseUrl}/stats`)
                .pipe(map((res) => res.data)),
        );
    }

    /**
     * Retrieves a single license by its ID (cached).
     *
     * @param id - Unique identifier of the license
     * @returns Observable of the license data
     */
    public getLicenseById(id: string): Observable<ApiLicense> {
        return this._sharedService.getCached(this._cache, `license-${id}`, () =>
            this._http
                .get<ApiSingleResponse<ApiLicense>>(`${this._baseUrl}/${id}`)
                .pipe(map((res) => res.data)),
        );
    }

    /**
     * Retrieves the playlist contents for a specific license.
     *
     * Requests `sortBy: 'sequence', sortOrder: 'asc'` so the backend orders the
     * response correctly when it honors these params. The caller still sorts
     * the result client-side as a guaranteed fallback, since the backend
     * doesn't yet reliably apply this ordering (see backend note).
     *
     * @param id - Unique identifier of the license
     * @returns Observable of the playlist contents payload
     */
    public getLicensePlaylistContents(
        id: string,
        params?: { page?: number; limit?: number },
    ): Observable<ApiGetAllResponse<ApiLicensePlaylistContent>> {
        return this._http.get<ApiGetAllResponse<ApiLicensePlaylistContent>>(
            `${this._baseUrl}/${id}/playlist-contents`,
            {
                params: buildQueryParams({
                    page: params?.page ?? 1,
                    limit: params?.limit ?? 10,
                    sortBy: 'sequence',
                    sortOrder: 'asc',
                }),
            },
        );
    }

    /**
     * Retrieves the device statuses (cached).
     *
     * @returns Observable of the device statuses
     */
    public getDeviceStatuses(id: string): Observable<DeviceStatus> {
        return this._sharedService.getCached(this._cache, `deviceStatuses-${id}`, () =>
            this._http
                .get<ApiSingleResponse<DeviceStatus>>(`${this._baseUrlDeviceStatuses}/${id}`)
                .pipe(map((res) => res.data)),
        );
    }

    /**
     * Retrieves the internet metrics (cached).
     *
     * @returns Observable of the internet metrics
     */
    public getInternetMetrics(id: string): Observable<InternetMetric | null> {
        return this._sharedService.getCached(this._cache, `internetMetrics-${id}`, () =>
            this._http
                .get<ApiGetAllResponse<InternetMetric>>(`${this._baseUrl}/${id}/internet-metrics`)
                .pipe(map((res) => res.data?.[0] ?? null)),
        );
    }

    /**
     * Retrieves the device info (cached).
     *
     * @returns Observable of the device info
     */
    public getDeviceInfo(id: string): Observable<DeviceInfo> {
        return this._sharedService.getCached(this._cache, `deviceInfo-${id}`, () =>
            this._http
                .get<ApiSingleResponse<DeviceInfo>>(`${this._baseUrlDeviceInfo}/${id}`)
                .pipe(map((res) => res.data)),
        );
    }

    /**
     * Retrieves all device info entries.
     *
     * @returns Observable of all device info
     */
    public getAllDeviceInfo(
        params?: LicenseQueryParams,
    ): Observable<ApiGetAllResponse<DeviceInfo>> {
        const queryParams = buildQueryParams(params);
        return this._http.get<ApiGetAllResponse<DeviceInfo>>(this._baseUrlDeviceInfo, {
            params: queryParams,
        });
    }

    /**
     * Retrieves the player info (cached).
     *
     * @returns Observable of the player info
     */
    public getPlayerInfo(id: string): Observable<PlayerInfo> {
        return this._sharedService.getCached(this._cache, `playerInfo-${id}`, () =>
            this._http
                .get<ApiSingleResponse<PlayerInfo>>(`${this._baseUrl}/${id}/player-info`)
                .pipe(map((res) => res.data)),
        );
    }

    /**
     * Retrieves the timestamp (cached).
     *
     * @returns Observable of the timestamp
     */
    public getTimestamp(id: string): Observable<LicenseTimestamp> {
        return this._sharedService.getCached(this._cache, `timestamp-${id}`, () =>
            this._http
                .get<ApiSingleResponse<LicenseTimestamp>>(`${this._baseUrlTimestamp}/${id}`)
                .pipe(map((res) => res.data)),
        );
    }

    /**
     * Clears the entire cache, or only entries for a specific license ID.
     * Call this when you need to force a fresh API fetch (e.g., after an update).
     *
     * @param id - Optional license ID. If provided, only that license's cache is cleared.
     */
    public clearCache(id?: string): void {
        if (id) {
            for (const key of this._cache.keys()) {
                if (key.endsWith(`-${id}`)) {
                    this._cache.delete(key);
                }
            }
        } else {
            this._cache.clear();
        }
    }

    /**
     * Creates a new license.
     *
     * @param data - License data to create
     * @returns Observable of the created license (includes server-generated fields)
     */
    public create(data: CreateLicenseDto): Observable<ApiLicense> {
        return this._http.post<ApiLicense>(this._baseUrl, data).pipe(tap(() => this.clearCache()));
    }

    /**
     * Updates an existing license.
     *
     * @param id - Unique identifier of the license to update
     * @param data - Partial license data to update
     * @returns Observable of the updated license
     */
    public update(id: string, data: UpdateLicenseDto): Observable<ApiLicense> {
        return this._http
            .put<ApiLicense>(`${this._baseUrl}/${id}`, data)
            .pipe(tap(() => this.clearCache(id)));
    }

    /**
     * Deletes a license by its ID.
     *
     * @param id - Unique identifier of the license to delete
     * @returns Observable of the deletion response
     */
    public delete(id: string): Observable<void> {
        return this._http
            .delete<void>(`${this._baseUrl}/${id}`)
            .pipe(tap(() => this.clearCache(id)));
    }

    /**
     * Deletes multiple licenses in a single request.
     *
     * @param ids - Array of license IDs to delete
     * @returns Observable of the bulk deletion response
     */
    public bulkDelete(ids: string[]): Observable<void> {
        return this._http
            .post<void>(`${this._baseUrl}/bulk-delete`, { licenseIds: ids })
            .pipe(tap(() => this.clearCache()));
    }

    /**
     * Creates a centralized reactive stream for fetching and searching licenses.
     *
     * @param config - The streams for search, refresh, and filters.
     * @returns Observable of mapped UI licenses
     */
    public getLicensesStream(config: {
        searchTerm$: Observable<string>;
        refreshTrigger$: Observable<number>;
        filters$: Observable<QueryFilter>;
        timezoneId$?: Observable<number | null>;
        page$?: Observable<number>;
        limit$?: Observable<number>;
    }): Observable<PaginatedResponse<UILicense>> {
        const debouncedSearch$ = config.searchTerm$.pipe(
            debounceTime(2000),
            distinctUntilChanged(),
        );
        const timezoneId$ = config.timezoneId$ || of(null);
        const page$ = config.page$ || of(1);
        const limit$ = config.limit$ || of(10);

        return combineLatest([
            debouncedSearch$,
            config.refreshTrigger$,
            config.filters$,
            timezoneId$,
            page$,
            limit$,
        ]).pipe(
            // Defer emissions to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError (NG0100)
            delay(0),
            switchMap(([searchTerm, _, apiFilters, timezoneId, page, limit]) => {
                // If timezone filter is active, resolve hostLocationIds first
                const hostLocationIds$ =
                    timezoneId !== null
                        ? fetchAllBatch<ApiHostLocation, BaseQueryParams>(
                              (p: BaseQueryParams) => this._hostsService.getAll(p),
                              { filters: { timezoneId: { _eq: timezoneId } } },
                              { maxRecords: 10000 },
                          ).pipe(
                              map((response: ApiHostLocation[]) =>
                                  response.map((host: ApiHostLocation) => host.hostLocationId),
                              ),
                              catchError((error) => {
                                  Logger.error(
                                      'LicensesService',
                                      'Error resolving host locations:',
                                      error,
                                  );
                                  return of([] as string[]);
                              }),
                          )
                        : of(null as string[] | null);

                return hostLocationIds$.pipe(
                    switchMap((hostLocationIds: string[] | null) => {
                        if (hostLocationIds !== null && hostLocationIds.length === 0) {
                            return of({
                                data: [],
                                total: 0,
                                page: 1,
                                pageSize: 0,
                            } as PaginatedResponse<UILicense>);
                        }

                        const finalFilters = { ...(apiFilters as Record<string, any>) };
                        if (hostLocationIds !== null) {
                            finalFilters['hostLocationId'] = { _in: hostLocationIds };
                        }

                        return this.getAll({
                            filters: finalFilters,
                            search: searchTerm || undefined,
                            searchFields: searchTerm ? LICENSE_SEARCH_FIELDS.join(',') : undefined,
                            page,
                            limit,
                        } as LicenseQueryParams).pipe(
                            catchError((error: unknown) => {
                                Logger.error('LicensesService', 'Error fetching licenses:', error);
                                // Return an empty API shape compatible with ApiGetAllResponse
                                return of({
                                    data: [],
                                    meta: {
                                        timestamp: '',
                                        statusCode: 500,
                                        pagination: {
                                            totalCount: 0,
                                            page: 1,
                                            limit: 0,
                                            totalPages: 0,
                                            hasNextPage: false,
                                            hasPreviousPage: false,
                                        },
                                    },
                                } as ApiGetAllResponse<ApiLicense>);
                            }),
                            map((res: ApiGetAllResponse<ApiLicense>) => {
                                const mapped = res.data.map((l: ApiLicense) => this.mapToUI(l));
                                return {
                                    data: mapped,
                                    total: res.meta?.pagination?.totalCount ?? mapped.length,
                                    page: res.meta?.pagination?.page ?? 1,
                                    pageSize: res.meta?.pagination?.limit ?? mapped.length,
                                } as PaginatedResponse<UILicense>;
                            }),
                        );
                    }),
                );
            }),
        );
    }

    /**
     * Maps the raw API license data to a flat UI-friendly format for the table component.
     *
     * @param license - The raw license data from the API.
     * @returns Mapped UI license object.
     */
    public mapToUI(license: ApiLicense): UILicense {
        const desk = license.anyDesk;
        const anydeskId =
            (desk?.id != null && String(desk.id).trim() !== '' ? String(desk.id).trim() : null) ??
            (license.deviceInfo?.anydeskId != null &&
            String(license.deviceInfo.anydeskId).trim() !== ''
                ? String(license.deviceInfo.anydeskId).trim()
                : null);

        const anydeskRows: UILicense['anydesk'] = [{ label: 'ID', value: anydeskId ?? null }];
        const pw = desk?.password != null ? String(desk.password).trim() : '';
        if (pw) {
            anydeskRows.push({ label: 'PW', value: pw });
        }

        const displayFromDevice =
            license.deviceStatus != null
                ? license.deviceStatus.displayStatus
                    ? 'ON'
                    : 'OFF'
                : undefined;
        const displayFromStatus = license.status?.statusLabel?.trim();

        const dealerLabel = (license.dealer ?? '').trim();
        const owner = license.owner;
        const ownerLabel =
            owner != null
                ? [owner.firstName, owner.lastName]
                      .filter((x) => (x ?? '').trim())
                      .join(' ')
                      .trim() ||
                  owner.emailAddress?.trim() ||
                  owner.username?.trim()
                : '';

        return {
            id: license.licenseId ?? '',
            screenshot: license.screenshotUrl ?? undefined,
            displayStatus: displayFromDevice ?? displayFromStatus ?? 'OFF',
            licenseKey: license.licenseKey ?? '',
            licenseKeyStatus: license.status,
            dealer: dealerLabel || ownerLabel || undefined,
            host: license.hostLocation?.hostLocationName,
            anydesk: anydeskRows,
            alias: license.alias ?? undefined,
            installationDate: license.installDate
                ? formatDateDisplay(new Date(license.installDate))
                : undefined,
            createdDate: license.createdAt
                ? formatDateDisplay(new Date(license.createdAt))
                : undefined,
            state: license.state,
        };
    }

    /**
     * Retrieves all generic checklists, with optional cache bypassing.
     *
     * @param {boolean} [forceRefresh=false] - If true, clears the local 'allChecklists' cache
     * and forces a fresh network request.
     *
     * @returns {Observable<ApiGetAllResponse<ApiChecklists>>} An observable containing the
     * paginated checklist response.
     */
    public getAllChecklists(forceRefresh = false): Observable<ApiGetAllResponse<ApiChecklists>> {
        if (forceRefresh) {
            this._cache.delete('allChecklists');
        }
        return this._sharedService.getCached(this._cache, 'allChecklists', () =>
            this._http.get<ApiGetAllResponse<ApiChecklists>>(
                `${this._baseUrlChecklist}?t=${new Date().getTime()}`,
            ),
        );
    }

    /**
     * Creates a new generic checklist.
     * @param data The payload containing checklist details.
     * @returns An observable of the created ApiChecklists instance.
     */
    public createChecklist(data: CreateChecklistDto): Observable<ApiChecklists> {
        return this._http
            .post<ApiChecklists>(this._baseUrlChecklist, data)
            .pipe(tap(() => this._cache.delete('allChecklists')));
    }

    /**
     * Updates an existing generic checklist by its ID.
     * @param id The unique identifier of the checklist to update.
     * @param data The payload containing updated checklist details.
     * @returns An observable of the updated ApiChecklists instance.
     */
    public updateChecklistById(id: string, data: CreateChecklistDto): Observable<ApiChecklists> {
        return this._http
            .patch<ApiChecklists>(`${this._baseUrlChecklist}/${id}`, data)
            .pipe(tap(() => this._cache.delete('allChecklists')));
    }

    /**
     * Deletes a generic checklist by its ID.
     * @param id The unique identifier of the checklist to delete.
     * @returns An observable that completes upon successful deletion.
     */
    public deleteChecklistById(id: string): Observable<void> {
        return this._http
            .delete<void>(`${this._baseUrlChecklist}/${id}`)
            .pipe(tap(() => this._cache.delete('allChecklists')));
    }

    /**
     * Retrieves all entity checklists.
     * @returns An observable containing the response wrapper with the array of entity checklists.
     */
    public getAllEntityChecklists(): Observable<ApiGetAllResponse<ApiEntityChecklist>> {
        return this._http.get<ApiGetAllResponse<ApiEntityChecklist>>(this._baseUrlEntityChecklist);
    }

    /**
     * Creates a new entity checklist (mapping a generic checklist to a specific entity target).
     * @param data The payload containing entity checklist details.
     * @returns An observable of the created ApiEntityChecklist instance.
     */
    public createEntityChecklist(data: CreateEntityChecklistDto): Observable<ApiEntityChecklist> {
        return this._http.post<ApiEntityChecklist>(this._baseUrlEntityChecklist, data);
    }

    /**
     * Updates an existing entity checklist by its ID.
     * @param id The unique identifier of the entity checklist to update.
     * @param data The payload containing updated entity checklist details.
     * @returns An observable of the updated ApiEntityChecklist instance.
     */
    public updateEntityChecklistById(
        id: string,
        data: CreateEntityChecklistDto,
    ): Observable<ApiEntityChecklist> {
        return this._http.patch<ApiEntityChecklist>(`${this._baseUrlEntityChecklist}/${id}`, data);
    }

    /**
     * Deletes an entity checklist by its ID.
     * @param id The unique identifier of the entity checklist to delete.
     * @returns An observable that completes upon successful deletion.
     */
    public deleteEntityChecklistById(id: string): Observable<void> {
        return this._http.delete<void>(`${this._baseUrlEntityChecklist}/${id}`);
    }

    public generateLicenses(data: { licenseCount: number; ownerId: string }) {
        return this._http.post(this._baseUrl, data).pipe(tap(() => this.clearCache()));
    }
}
